// Syft ACP - Lib <https://github.com/Syft-Application/syft2acp>
// © Syft Online Limited

import React, { useState, useMemo, useCallback, useEffect, FC } from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import { FormControl } from 'react-bootstrap'
import { debounce } from 'lodash-es'

import * as filtersActions from 'syft-acp-core/store/filters/actions'
import { useRegionIdPaste } from 'syft-acp-util/hooks/useRegionIdPaste'

import FilterUnit from '../FilterUnit'
import { setFilterValue } from './helpers'
import { FilterTextProps, Name, FilterTextConnected, ValueType } from './types'
import './FilterText.css'

// react-bootstrap types the paste target as the FormControl component rather
// than the underlying <input>, so the handler is cast to what it expects.
type FormControlPasteHandler = React.ComponentProps<typeof FormControl>['onPaste']

type UseFilterValueArgs = Pick<
  FilterTextProps,
  'name' | 'type' | 'min' | 'max' | 'minLength' | 'currentQuery' | 'actions' | 'onChange'
>

/** Owns the debounced filter value and the change handler for the input. */
const useFilterValue = ({ name, type, min, max, minLength, currentQuery, actions, onChange }: UseFilterValueArgs) => {
  const debouncedSetFilter = useMemo(
    () => debounce((filterName, filterValue) => actions.setFilter(filterName, filterValue), 300, { trailing: true }),
    [actions]
  )
  const [value, setValue] = useState<ValueType>(currentQuery || '')
  useEffect(() => {
    setValue(currentQuery)
  }, [currentQuery])

  const applyValue = useCallback(
    (filterValue: ValueType) => {
      setFilterValue({ name, filterValue, type, min, max, minLength, setValue, debouncedSetFilter })
      if (onChange) onChange(name, filterValue as string)
    },
    [min, max, minLength, type, debouncedSetFilter, name, onChange]
  )
  const handleChange = useCallback(ev => applyValue(ev?.target?.value), [applyValue])

  return { value, applyValue, handleChange }
}

/** Wraps the shared region-ID paste hook, gating it behind the `normalizeRegionId` opt-in. */
const useNormalizedPasteHandler = (
  enabled: boolean,
  applyValue: (value: ValueType) => void
): FormControlPasteHandler => {
  const onRegionIdPaste = useRegionIdPaste()

  return enabled
    ? ((event: React.ClipboardEvent<HTMLInputElement>) =>
        onRegionIdPaste(event, applyValue)) as unknown as FormControlPasteHandler
    : undefined
}

export const FilterText: FC<FilterTextProps> = ({
  name,
  placeholder,
  small = false,
  min = null,
  max = null,
  minLength = null,
  tiny = false,
  actions,
  currentQuery,
  disabled = false,
  type = 'text',
  onChange,
  normalizeRegionId = false,
}) => {
  const { value, applyValue, handleChange } = useFilterValue({
    name,
    type,
    min,
    max,
    minLength,
    currentQuery,
    actions,
    onChange,
  })
  const handlePaste = useNormalizedPasteHandler(normalizeRegionId, applyValue)

  return (
    <FilterUnit small={small} tiny={tiny}>
      <FormControl
        type={type}
        value={value || ''}
        placeholder={placeholder}
        onChange={handleChange}
        onPaste={handlePaste}
        disabled={disabled}
      />
    </FilterUnit>
  )
}

export default connect(
  (state: FilterTextConnected, { name }: { name: Name }) => ({
    currentQuery: state.routing.locationBeforeTransitions.query[name],
  }),
  dispatch => ({
    actions: bindActionCreators(filtersActions, dispatch),
  })
)(FilterText)
